import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from './lib/cn';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/**
 * Locks BACKGROUND scrolling while an open popover has it mounted.
 *
 * Wheel / touch scroll originating OUTSIDE the popover is cancelled, so neither
 * the page nor a horizontally-scrollable table can move under an open panel —
 * the popover stays anchored to its trigger instead of drifting or tearing the
 * grid sideways. Scrolling INSIDE the popover's own content still works.
 *
 * IMPORTANT: this MUST render inside `PopoverPrimitive.Content`, never in the
 * `PopoverContent` wrapper. The wrapper component sits in the React tree at all
 * times (only Radix's Portal gates its children on `open`), so an effect there
 * would attach these listeners for every popover on the page and never release
 * them — blocking scroll with nothing open.
 *
 * Listeners are capturing + non-passive so the background scroll can actually be
 * `preventDefault()`-ed.
 */
function ScrollLock({ contentRef }: { contentRef: React.RefObject<HTMLElement | null> }) {
  React.useEffect(() => {
    const prevent = (e: Event) => {
      const content = contentRef.current;
      if (content && e.target instanceof Node && content.contains(e.target)) return;
      // A Select/menu opened from inside the popover portals its list to
      // <body>, outside `content` — that overlay must stay scrollable; only
      // the page behind the popover is locked.
      if (
        e.target instanceof Element &&
        e.target.closest('[role="listbox"], [role="menu"]')
      )
        return;
      e.preventDefault();
    };
    const opts = { passive: false, capture: true } as const;
    document.addEventListener('wheel', prevent, opts);
    document.addEventListener('touchmove', prevent, opts);
    return () => {
      document.removeEventListener('wheel', prevent, true);
      document.removeEventListener('touchmove', prevent, true);
    };
  }, [contentRef]);
  return null;
}

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  /** Allow the page/table to scroll while this popover is open. Default `false`. */
  lockScroll?: boolean;
};

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      // Left-aligned to the trigger by default — popovers here are overwhelmingly
      // dropdowns (pickers, chip menus, inline editors), and a centred panel reads as
      // detached from the control that opened it. Pass `align="center"` to opt out.
      align = 'start',
      sideOffset = 4,
      collisionPadding = 8,
      lockScroll = true,
      children,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    // Compose the forwarded ref with the internal one the scroll-lock needs.
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={setRefs}
          align={align}
          sideOffset={sideOffset}
          // Keep the panel inside the viewport so opening one never grows the
          // page/container width (which would add a horizontal scrollbar).
          collisionPadding={collisionPadding}
          className={cn(
            'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-[--radix-popover-content-transform-origin] rounded-md p-4 shadow-overlay outline-none',
            className,
          )}
          {...props}
        >
          {/* Mounts ONLY while the popover is open (Portal gates its children). */}
          {lockScroll ? <ScrollLock contentRef={innerRef} /> : null}
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverArrow = PopoverPrimitive.Arrow;

const PopoverAnchor = PopoverPrimitive.Anchor;

export { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverAnchor };
